import { Injectable, Inject, PLATFORM_ID, inject, NgZone, computed, Signal, OnDestroy } from '@angular/core';
import { interval, BehaviorSubject, Observable, Subscription } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { format, toZonedTime } from 'date-fns-tz';
import { de } from 'date-fns/locale';
import { isHoliday } from 'feiertagejs';
import { WebsiteStateService } from '../state-management/services/website-state.service';
import { OpeningHours } from '../state-management/model/interfaces/state.model';
import { GermanState } from '../state-management/model/enums/state.enums';

export enum OpeningHoursStatus {
  Undefined = 'Unbekannt',
  Closed = 'Geschlossen',
  ClosingSoon = 'Schließt bald',
  Open = 'Offen'
}

function toGermanState(state: string | null): GermanState | null {
  if (state && Object.values(GermanState).includes(state as GermanState)) {
      return state as GermanState;
  }
  return null; // Falls der state nicht im Enum existiert, gibt null zurück
}

export function getStateAbbreviation(state: GermanState): "BW" | "BY" | "BE" | "BB" | "HB" | "HE" | "HH" | "MV" | "NI" | "NW" | "RP" | "SL" | "SN" | "ST" | "SH" | "TH" {
  switch (state) {
      case GermanState.BadenWuerttemberg:
          return "BW";
      case GermanState.Bayern:
          return "BY";
      case GermanState.Berlin:
          return "BE";
      case GermanState.Brandenburg:
          return "BB";
      case GermanState.Bremen:
          return "HB";
      case GermanState.Hamburg:
          return "HH";
      case GermanState.Hessen:
          return "HE";
      case GermanState.Niedersachsen:
          return "NI";
      case GermanState.MecklenburgVorpommern:
          return "MV";
      case GermanState.NordrheinWestfalen:
          return "NW";
      case GermanState.RheinlandPfalz:
          return "RP";
      case GermanState.Saarland:
          return "SL";
      case GermanState.Sachsen:
          return "SN";
      case GermanState.SachsenAnhalt:
          return "ST";
      case GermanState.SchleswigHolstein:
          return "SH";
      case GermanState.Thueringen:
          return "TH";
      default:
          throw new Error("Unbekanntes Bundesland");
  }
}

@Injectable({
  providedIn: 'root'
})
export class OpeningHoursService implements OnDestroy {
  private subscription: Subscription = new Subscription();
  private statusSubject = new BehaviorSubject<OpeningHoursStatus | undefined>(undefined);
  public status$: Observable<OpeningHoursStatus | undefined> = this.statusSubject.asObservable();

  private configuration = this.websiteState.configuration;
  private openingHours: Signal<OpeningHours> = computed(() => this.configuration().openingHours);

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,  private websiteState: WebsiteStateService ) {

    this.updateStatus();
    if (isPlatformBrowser(this.platformId)) {
      inject(NgZone).runOutsideAngular(() => {
        this.subscription = interval(1000).subscribe(() => {
          this.updateStatus()
        });
      })
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private updateStatus() {
    const status = this.calculateStatus('Europe/Berlin');
    this.statusSubject.next(status);
  }

  private calculateStatus(timezone: string): OpeningHoursStatus | undefined {

    // Hilfsfunktion zur Berechnung des Status basierend auf Zeit
    const getStatusForPeriods = (periods: { open: string; close: string }[]): OpeningHoursStatus => {
      const closingSoonThreshold = 30; // Minuten vor Schließung
      let status = OpeningHoursStatus.Closed;

      periods.forEach(period => {
        const [openHours, openMinutes] = period.open.split(':').map(Number);
        const [closeHours, closeMinutes] = period.close.split(':').map(Number);
        const openTime = openHours * 60 + openMinutes;
        const closeTime = closeHours * 60 + closeMinutes;

        if (currentTime >= openTime && currentTime <= closeTime) {
          status = currentTime >= (closeTime - closingSoonThreshold) ? OpeningHoursStatus.ClosingSoon : OpeningHoursStatus.Open;
        }
      });
      return status;
    };

    // const now = new Date(2024, 11, 26, 11, 0, 0);
    const now = new Date();
    const zonedTime = toZonedTime(now, timezone);
    const currentDate = format(zonedTime, 'dd.MM.yyyy', { timeZone: timezone, locale: de });
    const currentDay = format(zonedTime, 'EEEE', { timeZone: timezone, locale: de });
    const currentTime = zonedTime.getHours() * 60 + zonedTime.getMinutes(); // Minuten seit Mitternacht
    const todaySpecialHours = this.openingHours().specialDayOpeningHours.find(hours => hours.date === currentDate);
    const todayRegularHours = this.openingHours().regularOpeningHours.find(hours => hours.day === currentDay);
    const germanState = toGermanState(this.configuration().contactData.state);

    if (todaySpecialHours) {
      console.log("TODAY SPECIAL HOURS:", todaySpecialHours);
      if (!todaySpecialHours || todaySpecialHours.periods.length === 0) {
        return OpeningHoursStatus.Closed; // Closed today
      }
      else {
        return getStatusForPeriods(todaySpecialHours.periods); 
      }
    }

    else if(germanState && isHoliday(now, getStateAbbreviation(germanState)) && this.openingHours().holidayOpeningHours.stateHolidaySync) {
      return getStatusForPeriods(this.openingHours().holidayOpeningHours.defaultPeriods);
    }

    else {
      if (!todayRegularHours || todayRegularHours.periods.length === 0) {
        return OpeningHoursStatus.Closed; // Closed today
      }
      else {
        return getStatusForPeriods(todayRegularHours.periods);
      }
    }
  }
}